import { IS_TAURI } from '../config';

export interface OidcTokenExchangeResult {
  status: number;
  body: unknown;
}

export interface OidcTokenExchangeArgs {
  tokenEndpoint: string;
  params: Record<string, string>;
}

export interface TauriInternals {
  invoke: <T = unknown>(
    command: string,
    args?: Record<string, unknown>
  ) => Promise<T>;
}

declare global {
  interface Window {
    __TAURI_INTERNALS__?: TauriInternals;
  }
}

/** Minimal response shape shared by browser fetch and the Tauri command. */
export interface OidcHttpResult {
  status: number;
  ok: boolean;
  json: () => Promise<unknown>;
}

function paramsRecord(params: URLSearchParams): Record<string, string> {
  const record: Record<string, string> = {};
  params.forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

/**
 * POST an OAuth token request. The desktop shell uses the Rust command so the
 * IdP does not need to allow the Tauri origin in CORS.
 */
export async function postOidcTokenRequest(
  tokenEndpoint: string,
  params: URLSearchParams
): Promise<OidcHttpResult> {
  if (IS_TAURI) {
    const invoke = window.__TAURI_INTERNALS__?.invoke;
    if (!invoke) {
      throw new Error('Tauri command bridge is unavailable');
    }
    const result = await invoke<OidcTokenExchangeResult>('oidc_token_exchange', {
      tokenEndpoint,
      params: paramsRecord(params),
    });
    const body = result.body;
    return {
      status: result.status,
      ok: result.status >= 200 && result.status < 300,
      json: async () => body,
    };
  }

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: params.toString(),
  });

  return {
    status: response.status,
    ok: response.ok,
    json: async () => response.json(),
  };
}
